import type { MatchingPropertiesData, MatchPropertyField } from '../types';
import { TextWithNAFallback } from '@/components';
import { MatchPropertyDot } from './MatchPropertyDot';

// `field` is a camelCase identifier from the connector; map known MATCH fields to
// their display labels and PascalCase-ize anything else.
const FIELD_LABELS: Record<string, string> = {
  dba: 'DBA',
  urls: 'URLs',
  phoneNumber: 'PhoneNumber',
  nationalTaxId: 'NationalTaxId',
  address: 'Address',
  name: 'Name',
};

const formatFieldLabel = (field: string) =>
  FIELD_LABELS[field] ?? field.charAt(0).toUpperCase() + field.slice(1);

const PropertyRow = ({ field, value, matchType }: MatchPropertyField) => (
  <div className="flex items-start gap-2 py-1">
    <span className="w-32 shrink-0 text-sm font-medium text-slate-700">
      {formatFieldLabel(field)}
    </span>
    <MatchPropertyDot matchType={matchType} className="mt-1.5" />
    <TextWithNAFallback className="text-sm text-slate-900" disableTranslation>
      {value}
    </TextWithNAFallback>
  </div>
);

/**
 * The expandable "Matching Properties" block on a matched record — which
 * merchant fields and which principals matched, with exact/partial dots.
 */
export const MatchingProperties = ({
  matchingProperties,
}: {
  matchingProperties?: MatchingPropertiesData | null;
}) => {
  if (!matchingProperties) {
    return null;
  }

  const { merchantInformation, principals } = matchingProperties;

  return (
    <div className="space-y-6 p-2">
      <h4 className="text-base font-bold">Matching Properties</h4>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        <div>
          <h5 className="mb-2 text-sm font-bold">Merchant Information</h5>
          {merchantInformation.map(prop => (
            <PropertyRow key={prop.field} {...prop} />
          ))}
        </div>
        <div className="space-y-4">
          {principals.map(principal => (
            <div key={principal.label}>
              <h5 className="mb-2 text-sm font-bold">{principal.label}</h5>
              {principal.fields.map((fieldProp, index) => (
                <PropertyRow key={`${fieldProp.field}-${index}`} {...fieldProp} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
